import { Fade } from 'reactstrap';

// Workaround for a bug in reactstrap 9.2.x:
//
// reactstrap/src/Fade.js declares a local `defaultProps` constant but never
// assigns it to `Fade.defaultProps`, so consumers that fill their own default
// `transition` from `{...Fade.defaultProps}` see `undefined`, and
// react-transition-group's required `Transition.propTypes.timeout` then warns.
//
// Applying the defaults reactstrap *intended* to set fixes the consumers that
// read `Fade.defaultProps` at render: a direct `<Fade>`, and `Alert`
// (`src/Alert.js` spreads it inside render). It does NOT silence `PopperContent`
// (the Tooltip/Popover body): its `transition` prop defaults to a `{...Fade.defaultProps}`
// copy captured when its module first loads — before this patch runs — so that default is
// empty and its `timeout` fails react-transition-group's required propType. (PopperContent
// re-spreads `Fade.defaultProps` at render, which would pick up the patched value, but then
// overrides `timeout` with `fade ? transition.timeout : 0`, reading that same empty import-time
// default.) So Tooltip/Popover keep emitting the `transition.timeout` PropType warning, which
// PanelSite's test suppression list (`test/traps/consoleError.ts`) covers. The object below
// mirrors reactstrap/src/Fade.js:33-40 exactly:
// `react-transition-group`'s `Transition.defaultProps` (the `mountOnEnter`,
// `unmountOnExit`, and `onEnter*`/`onExit*` noops) followed by Fade's explicit
// overrides (`timeout: 150` matches `TransitionTimeouts.Fade`). We inline the
// values rather than importing `Transition` because `react-transition-group`
// ships no type declarations in this project; the values are a stable,
// documented part of its v4 public API.
//
// Importing this module for side effects at app startup is sufficient.

type FadeWithDefaults = typeof Fade & { defaultProps?: Record<string, unknown> };
const FadeCtor = Fade as FadeWithDefaults;

const noop = () => {
    /* intentionally empty */
};

if (FadeCtor.defaultProps === undefined) {
    FadeCtor.defaultProps = {
        // from Transition.defaultProps
        mountOnEnter: false,
        unmountOnExit: false,
        enter: true,
        exit: true,
        onEnter: noop,
        onEntering: noop,
        onEntered: noop,
        onExit: noop,
        onExiting: noop,
        onExited: noop,
        // reactstrap's explicit overrides in Fade.js
        timeout: 150,
        appear: true,
        in: true,
    };
}
